<template>
    <StepShell :embedded="embedded">
        <template v-if="!hasProvider && !showConnected">
            <StepHeader
                :embedded="embedded"
                align="center"
                size="display"
                :title="t('onboarding.calendarFirst.calendarConnectStep.title')"
                :source="embedded ? 'calendar' : undefined"
                :lede="t('onboarding.calendarFirst.calendarConnectStep.noProviderLede', { organization: organizationName })" />

            <StepActions flush>
                <BaseButton theme="primary" :disabled="busy" @click="emit('skip')">{{ t("onboarding.calendarFirst.actions.continue") }}</BaseButton>
            </StepActions>
        </template>

        <template v-else>
            <StepHeader
                :embedded="embedded"
                align="center"
                size="display"
                :title="t('onboarding.calendarFirst.calendarConnectStep.title')"
                :source="embedded ? 'calendar' : undefined">
                <template v-if="showConnected">{{ t("onboarding.profileSetup.calendarLast.calendar.connectedLede") }}</template>
                <template v-else-if="calendarLast && personalized">{{ t("onboarding.profileSetup.calendarLast.calendar.lede") }}</template>
                <template v-else>{{ t("onboarding.calendarFirst.calendarConnectStep.lede", { days: SCAN_WINDOW_DAYS }, SCAN_WINDOW_DAYS) }}</template>

                <template v-if="showConnected" #titleSuffix>
                    <StatusChip state="done">
                        <template #leading><i class="bi bi-check-lg" aria-hidden="true"></i></template>
                        {{ t("onboarding.profileSetup.calendarLast.calendar.connected") }}
                    </StatusChip>
                </template>

                <template #note>
                    <BaseCollapsible class="disclosure" :class="{ 'is-embedded': embedded }">
                        <template #trigger>
                            <span class="disclosure-summary">
                                <i aria-hidden="true" class="bi bi-lock-fill"></i>
                                <span>{{ t("onboarding.calendarFirst.calendarConnectStep.disclosure.summary") }}</span>
                                <i aria-hidden="true" class="bi bi-chevron-down disclosure-chevron"></i>
                            </span>
                        </template>

                        <template #content>
                            <!-- The separate panel prevents expanded disclosure content from blending into the
                                 rest of the step.
                                 -->
                            <div class="disclosure-panel">
                                <!-- Keep organization approval inside the disclosure to avoid duplicating
                                     reassurance in a separate box.
                                     -->
                                <p v-if="!showConnected" class="sanction">
                                    <i aria-hidden="true" class="bi bi-patch-check-fill sanction-icon"></i>
                                    <span>{{ t("onboarding.calendarFirst.calendarConnectStep.disclosure.sanction", { organization: organizationName }) }}</span>
                                </p>

                                <p class="disclosure-heading">{{ t("onboarding.calendarFirst.calendarConnectStep.disclosure.heading") }}</p>

                                <ul class="disclosure-list">
                                    <li v-for="item in reads" :key="item" class="disclosure-item">
                                        <i aria-hidden="true" class="bi bi-check-lg reads-icon"></i>
                                        <span class="reads-text">{{ item }}</span>
                                    </li>
                                    <li v-for="item in avoids" :key="item" class="disclosure-item">
                                        <i aria-hidden="true" class="bi bi-x avoids-icon"></i>
                                        <span class="avoids-text">{{ item }}</span>
                                    </li>
                                </ul>
                            </div>
                        </template>
                    </BaseCollapsible>
                </template>
            </StepHeader>

            <!-- Show representative output before requesting access. Use two cards to communicate that
                 insights form a feed.
                 -->
            <figure class="sample">
                <figcaption class="sample-label">{{ t("onboarding.calendarFirst.calendarConnectStep.samples.label") }}</figcaption>
                <InsightCardLayout v-for="reading in sampleReadings" :key="reading.headline">
                    <h3 class="sample-headline">{{ reading.headline }}</h3>
                    <p class="sample-body">{{ reading.claim }} {{ reading.rest }}</p>
                </InsightCardLayout>
            </figure>

            <!--
                Confirm the timezone, as the calendar settings modal does: an incorrect inferred zone shifts
                meeting times throughout the insights.
            -->
            <div v-if="!showConnected" class="timezone">
                <p v-if="!editingTimezone" class="timezone-line">
                    {{ t("onboarding.calendarFirst.calendarConnectStep.timezone.shownIn", { zone: timezoneName }) }}
                    <BaseButton theme="tertiary" size="sm" class="timezone-change" @click="editingTimezone = true">{{
                        t("onboarding.calendarFirst.calendarConnectStep.timezone.change")
                    }}</BaseButton>
                </p>
                <label v-else class="timezone-label" for="onboarding-timezone">
                    {{ t("onboarding.calendarFirst.calendarConnectStep.timezone.label") }}
                    <BaseCombobox v-model="selectedTimezone" input-id="onboarding-timezone" :options="timezoneOptions" :disabled="busy" :clearable="false" />
                </label>
            </div>

            <StepActions flush>
                <!-- Linear onboarding needs skip to let users proceed without connecting. Guided setup
                     already provides navigation through its section rail.
                     -->
                <BaseButton v-if="!showConnected && (skippable ?? !embedded)" theme="tertiary" class="step-skip" :disabled="busy" @click="emit('skip')">{{
                    t("onboarding.calendarFirst.actions.skipForNow")
                }}</BaseButton>
                <BaseButton v-if="showConnected" theme="primary" :disabled="busy" @click="emit('continue')">{{
                    t("onboarding.profileSetup.calendarLast.calendar.seeInsights")
                }}</BaseButton>
                <BaseButton v-else theme="primary" :disabled="busy || selectedTimezone === null" @click="emit('connect', selectedTimezone!.value)">{{ connectLabel }}</BaseButton>
            </StepActions>
        </template>
    </StepShell>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import InsightCardLayout from "~vue/components/InsightCardLayout.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { activeLocale } from "~vue/utils/formatDate";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import StepActions from "./StepActions.vue";
import StepHeader from "./StepHeader.vue";
import StepShell from "./StepShell.vue";
import type { CalendarContext } from "./types";

const {
    embedded = false,
    skippable = undefined,
    calendar,
    organizationName,
    busy,
    calendarLast = false,
    personalized = false,
} = defineProps<{
    embedded?: boolean;
    skippable?: boolean;
    calendar: CalendarContext;
    organizationName: string;
    busy: boolean;
    calendarLast?: boolean;
    /** Calendar-last with Perspective and the profile done, so the pitch can promise combined insights. */
    personalized?: boolean;
}>();

const emit = defineEmits<{ connect: [timezone: string]; skip: []; continue: [] }>();

const { t } = useI18n();

const showConnected = computed(() => calendarLast && calendar.connected);

interface TimezoneOption {
    label: string;
    value: string;
}

const timezoneOptions = computed<TimezoneOption[]>(() => calendar.timezones.map((zone) => ({ label: zone, value: zone })));

/* The browser's guess is a prefill, not a silent write: it fills the field so the
   user confirms one they can see, and nothing is saved until they press Connect. */
const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";

function initialTimezone(): TimezoneOption | null {
    const known = new Set(calendar.timezones);
    for (const candidate of [calendar.timezone, browserTimezone]) {
        if (candidate && known.has(candidate)) {
            return { label: candidate, value: candidate };
        }
    }
    return null;
}

const selectedTimezone = ref<TimezoneOption | null>(initialTimezone());

const editingTimezone = ref(selectedTimezone.value === null);

const timezoneName = computed(() => {
    const zone = selectedTimezone.value?.value;
    if (!zone) {
        return t("onboarding.calendarFirst.calendarConnectStep.timezone.localFallback");
    }
    const city = zone.split("/").at(-1)?.replaceAll("_", " ") ?? zone;
    const label = new Intl.DateTimeFormat(activeLocale(), { timeZone: zone, timeZoneName: "long" }).formatToParts(new Date()).find((part) => part.type === "timeZoneName")?.value;
    return label ? `${label} (${city})` : city;
});

const connectLabel = computed(() => {
    if (busy) {
        return t("onboarding.calendarFirst.calendarConnectStep.connect.opening", { provider: calendar.provider_label });
    }
    return selectedTimezone.value === null
        ? t("onboarding.calendarFirst.calendarConnectStep.connect.pickTimezone")
        : t("onboarding.calendarFirst.calendarConnectStep.connect.connect", { provider: calendar.provider_label });
});

/* Mirrors `coach_core.onboarding.calendar_first.SCAN_WINDOW_DAYS`. The payoff
   screen quotes the window the scan actually used, from its own stats. */
const SCAN_WINDOW_DAYS = 30;

/*
 * A privacy commitment, not marketing copy — so every line is what the scan
 * really requests. The prototype claimed event notes were read; they are not.
 * Titles are, but only inside the aggregation that groups a standing meeting's
 * occurrences: no title reaches a prompt, a reading, or another person. The
 * OAuth request sets `allow_private_events=false`, which is what makes the
 * private/personal exclusion below a real guarantee rather than a promise.
 */
/* Keep sample insights within the scan's actual capabilities. */
const sampleReadings = computed(() => {
    if (calendarLast && personalized) {
        return [
            {
                headline: t("onboarding.profileSetup.calendarLast.calendar.samples.style.headline"),
                claim: t("onboarding.profileSetup.calendarLast.calendar.samples.style.body"),
                rest: "",
            },
            {
                headline: t("onboarding.profileSetup.calendarLast.calendar.samples.colleague.headline"),
                claim: t("onboarding.profileSetup.calendarLast.calendar.samples.colleague.body"),
                rest: "",
            },
        ];
    }
    return [
        {
            headline: t("onboarding.calendarFirst.calendarConnectStep.samples.tuesdays.headline"),
            claim: t("onboarding.calendarFirst.calendarConnectStep.samples.tuesdays.claim"),
            rest: t("onboarding.calendarFirst.calendarConnectStep.samples.tuesdays.rest"),
        },
        {
            headline: t("onboarding.calendarFirst.calendarConnectStep.samples.standing.headline"),
            claim: t("onboarding.calendarFirst.calendarConnectStep.samples.standing.claim"),
            rest: t("onboarding.calendarFirst.calendarConnectStep.samples.standing.rest"),
        },
    ];
});

const reads = computed(() => [
    t("onboarding.calendarFirst.calendarConnectStep.disclosure.reads.attendees"),
    t("onboarding.calendarFirst.calendarConnectStep.disclosure.reads.duration"),
    t("onboarding.calendarFirst.calendarConnectStep.disclosure.reads.titles"),
]);

const avoids = computed(() => [
    t("onboarding.calendarFirst.calendarConnectStep.disclosure.avoids.notes"),
    t("onboarding.calendarFirst.calendarConnectStep.disclosure.avoids.private"),
]);

const hasProvider = computed(() => calendar.provider.length > 0);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .disclosure-panel {
        @apply mt-2.5 flex flex-col gap-3 rounded-lgr bg-surface-sunken p-4;
    }

    .sanction {
        @apply body-small flex items-start gap-2.5 text-ink;
    }

    .disclosure-summary > .bi-lock-fill {
        @apply shrink-0;
    }

    .sanction-icon {
        @apply shrink-0 text-ink-muted;
    }

    .timezone {
        @apply -mt-4 flex w-full flex-col gap-2;
    }

    .timezone-line {
        @apply details flex items-center gap-1 text-ink-muted;
    }

    .timezone-change {
        @apply details -ml-1 text-[12px] leading-normal font-normal text-chip-nadia-text;
    }

    .timezone-label {
        @apply body-small-semibold flex flex-col gap-2 text-ink;
    }

    .sample {
        @apply flex w-full flex-col gap-2;
    }

    .sample-label {
        @apply scaffold-details-small text-ink-muted;
    }

    .sample-headline {
        @apply nadia-subtitle text-ink;
    }

    .sample-body {
        @apply body-small mt-2 text-ink-muted;
    }

    .disclosure {
        @apply mt-6 w-full;
    }

    .disclosure.is-embedded {
        @apply mt-0;
    }

    .disclosure-summary {
        @apply details inline-flex w-auto cursor-pointer items-center gap-1 text-ink-muted hover:text-ink;
    }

    .disclosure-chevron {
        @apply shrink-0 text-chip-nadia-text transition-transform;
    }

    .disclosure[data-state="open"] .disclosure-chevron {
        @apply rotate-180;
    }

    /* Extra space above the heading groups it with the list rather than the preceding paragraph. */
    .disclosure-heading {
        @apply details mt-1 tracking-wide text-ink-muted uppercase;
    }

    .disclosure-list {
        @apply -mt-1 flex flex-col gap-2;
    }

    .disclosure-item {
        @apply flex items-start gap-2.5;
    }

    .reads-icon {
        @apply shrink-0 text-ink-muted;
    }

    .reads-text {
        @apply body-small text-ink;
    }

    .avoids-icon {
        @apply shrink-0 text-ink-muted;
    }

    .avoids-text {
        @apply body-small text-ink-muted;
    }
}

@media (prefers-reduced-motion: reduce) {
    .disclosure-chevron {
        transition: none;
    }
}
</style>
